<template>
  <div class="branch-selection tw-flex tw-flex-col tw-items-center tw-mt-8">
    <div class="tw-w-full">
      <h2 style="color: #114cac;" class="tw-text-2xl tw-font-semibold tw-mb-4">
        Find & Select Branch to handle your business
      </h2>

      <div class="tw-flex tw-flex-wrap lg:tw-flex-nowrap tw-items-start tw-gap-4 tw-mb-6">
  <!-- Branch Selection Input -->
  <div class="tw-flex-1 lg:tw-flex-[3_3_30%] tw-bg-white tw-rounded-lg tw-shadow-md tw-p-4 tw-text-gray-700">
    <label class="tw-font-semibold tw-text-blue-700 tw-mb-2 block" for="branch-select">
      <i class="mdi mdi-magnify tw-mr-2"></i> Find & Select Branch
    </label>
    <v-form ref="form">
    <v-select
      v-model="selectedBranch"
      :items="branches"
      item-text="name"
      item-value="code"
      label="Search and select a branch"
      class="tw-w-full"
      @input="updateBranchInfo"
      :rules="[required('Search and select a branch')]"
    ></v-select>
  </v-form>
  </div>

  <!-- View on Map Button -->
  <div class="lg:w-[150px] lg:h-[100px] tw-bg-blue-100 tw-rounded-lg tw-shadow-md tw-flex tw-items-center tw-justify-center">
    <button
      @click="viewOnMap"
      class="tw-bg-blue-100 tw-text-blue-700 tw-w-full tw-h-full tw-rounded-md tw-shadow-md hover:tw-shadow-lg tw-flex tw-flex-col tw-items-center tw-justify-center"
    >
      <i class="mdi mdi-map-marker tw-text-3xl"></i>
      <span class="tw-text-sm tw-font-semibold">View on Map</span>
    </button>
  </div>

  <!-- Branch Information Box -->
  <div
    v-if="branchInfo"
    class="tw-flex-1 lg:tw-flex-[3_3_65%] tw-bg-blue-100 tw-rounded-lg tw-shadow-md tw-p-4 tw-text-gray-700"
  >
    <h3 class="tw-font-semibold tw-text-blue-700 tw-mb-2">
      <i class="mdi mdi-information-outline tw-mr-2"></i> Branch Information
    </h3>
    <p><strong>Name:</strong> {{ branchInfo.name }}</p>
    <p><strong>Location:</strong> {{ branchInfo.address }}</p>
    <p><strong>Open:</strong> {{ branchInfo.open }}</p>
    <p><strong>Close:</strong> {{ branchInfo.close }}</p>
    <p>
      <strong>Phone:</strong>
      <a :href="'tel:' + branchInfo.tel" class="tw-text-blue-600 tw-underline">{{ branchInfo.tel }}</a>
    </p>
  </div>
</div>

      <!-- Map Section -->
      <div class="tw-w-full tw-h-64 tw-rounded-lg tw-mt-4 tw-shadow-md">
        <iframe
          v-if="branchInfo && branchInfo.latitude && branchInfo.longitude"
          :src="mapUrl"
          class="tw-w-full tw-h-full tw-rounded-lg"
          style="border:0"
          allowfullscreen
          title="Selected Branch Location on Map"
        ></iframe>
      </div>

      <v-card-actions class="tw-flex tw-justify-center col-sm-4 offset-sm-4 tw-mt-5 tw-pb-8">
        <v-btn
          block
          large
          color="primary"
          class="my-3"
          @click="submitBranchSelection"
          :disabled="!selectedBranch"
        >
          {{ selectedBranch ? 'Next' : 'Check if all fields are filled' }}
        </v-btn>
      </v-card-actions>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { log } from '@/utils/errorLogger';

export default {
  name: 'BranchSelection',
  data() {
    return {
      selectedBranch: null,
      branches: [],
      branchInfo: null,
    };
  },
  computed: {
    mapUrl() {
      if (!this.branchInfo) return null;
      const baseUrl = 'https://www.google.com/maps/embed/v1/view';
      const key = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;
      const { latitude, longitude } = this.branchInfo;
      return `${baseUrl}?key=${key}&center=${latitude},${longitude}&zoom=15`;
    },
  },
  watch: {
    selectedBranch(newBranchCode) {
      if (newBranchCode) {
        const selectedBranch = this.branches.find((branch) => branch.code === newBranchCode);
        localStorage.setItem('selectedBranch', JSON.stringify(selectedBranch));
      } else {
        localStorage.removeItem('selectedBranch');
      }
    },
  },
  methods: {
    async fetchBranches() {
      try {
        const response = await axios.post('/motor/api/branch/list');
        if (response.data.code === 0) {
          this.branches = response.data.data.map((branch) => ({
            name: branch.name,
            address: branch.address,
            tel: branch.tel,
            latitude: branch.latitude,
            longitude: branch.longitude,
            code: branch.code,
            close: branch.closingTime,
            open: branch.openingTime,
          }));
        }
      } catch (error) {
        log(error, this, 'fetchBranches');
        this.$toast.error('Error fetching branches. Please try again.');
      }
    },
    updateBranchInfo(branchCode) {
      if (!branchCode) {
        this.branchInfo = null;
        return;
      }
      // Find the full branch object based on the selected code
      this.branchInfo = this.branches.find((branch) => branch.code === branchCode);
    },
    requestBranchInfo() {
      this.$toast.info('Feature to find the nearest branch is under development.');
    },
    async submitBranchSelection() {
      if (!this.selectedBranch) {
        this.$toast.error('Please select a branch first.');
        return;
      }
      const formPayload = JSON.parse(localStorage.getItem('formPayload')) || {};
      const customerId = formPayload.customerId;
      const quoteId = localStorage.getItem('quoteId') || null;

      try {
        localStorage.setItem('selectedBranch', JSON.stringify(this.branchInfo));
        const payload = {
          branch: this.branchInfo,
          customerId,
          policyNo: quoteId,
          quoteId,
        };
        const response = await axios.post('/motor/api/policy/sumbitBranch', payload);
        if (response.data.code === 0) {
          this.$toast.success('Branch selection submitted successfully.');
          this.$emit('next');
        }
      } catch (error) {
        log(error, this, 'submitBranchSelection');
        this.$toast.error('Error submitting branch selection. Please try again.');
      }
    },
    viewOnMap() {
      if (!this.branchInfo) {
        this.$toast.error('Please select a branch to view on the map.');
      } else {
        window.open(
          `https://www.google.com/maps?q=${this.branchInfo.latitude},${this.branchInfo.longitude}`,
          '_blank',
        );
      }
    },
    validateForm() {
      this.$nextTick(() => {
        this.$refs.form.validate();
      });
    },
    required(field) {
      return (v) => !!v || `${field} is required`;
    },
  },
  async mounted() {
    await this.fetchBranches();
    const storedBranch = localStorage.getItem('selectedBranch');
    if (storedBranch) {
      const parsedBranch = JSON.parse(storedBranch);
      this.selectedBranch = parsedBranch.code;
      this.branchInfo = parsedBranch;
    }
    this.validateForm();
  },
};
</script>

<style scoped>
.branch-selection {
  padding: 2rem;
  background-color: #f9f9f9;
}

button {
  transition: all 0.3s ease;
}
.v-select {
  border: transparent;
  border-radius: 8px;
}
</style>
